import React, { useState, useEffect } from "react";
import { Map, Marker } from "@vis.gl/react-google-maps";
import { Icon } from "semantic-ui-react";

interface CustomMapProps {
    height: string;
    width: string;
    onLocationChange: (latitude: number, longitude: number) => void;
    initialLatitude?: number | null;
    initialLongitude?: number | null;
    enableCurrentLocation?: boolean;
    draggable?: boolean;
}

const CustomMap = ({
    height,
    width,
    onLocationChange,
    initialLatitude,
    initialLongitude,
    enableCurrentLocation = false,
    draggable = true
}: CustomMapProps) => {

    const [markerLocation, setMarkerLocation] = useState({
        lat: initialLatitude != null ? initialLatitude : 25.68435,
        lng: initialLongitude != null ? initialLongitude : -100.31721,
    });

    const [zoom, setZoom] = useState(15);
    const [mapCenter, setMapCenter] = useState(markerLocation);

    const handleRecenter = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newLoc = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setMarkerLocation(newLoc);
                    setMapCenter(newLoc);
                    setZoom(17);
                },
                (error) => {
                    console.error("Error getting location: ", error);
                }
            );
        }
    };

    useEffect(() => {
        if (enableCurrentLocation && navigator.geolocation && initialLatitude == null && initialLongitude == null) {
            handleRecenter();
        }
    }, [enableCurrentLocation, initialLatitude, initialLongitude]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // If initial coords change, update the marker and recenter
        if (initialLatitude != null && initialLongitude != null) {
            const newLoc = {
                lat: initialLatitude,
                lng: initialLongitude
            };
            setMarkerLocation(newLoc);
            setMapCenter(newLoc); // Recenter map on new props
        }
    }, [initialLatitude, initialLongitude]);

    useEffect(() => {
        onLocationChange(markerLocation.lat, markerLocation.lng);
    }, [markerLocation, onLocationChange]);

    return (
        <div style={{ width: width, height: height, marginTop: "10px", marginBottom: "20px", position: "relative" }}>
            <Map
                style={{ borderRadius: "20px", width: "100%", height: "100%" }}
                zoom={zoom}
                center={mapCenter}
                onCameraChanged={(e) => {
                    setZoom(e.detail.zoom);
                    setMapCenter(e.detail.center);
                }}
                gestureHandling={"greedy"}
                disableDefaultUI
            >
                <Marker
                    position={markerLocation}
                    draggable={draggable}
                    onDragEnd={(e: any) => {
                        const newLoc = {
                            lat: e.latLng.lat(),
                            lng: e.latLng.lng(),
                        };
                        setMarkerLocation(newLoc);
                    }}
                />
            </Map>

            {/* Custom Zoom Controls for Tablet usability */}
            <div style={{
                position: "absolute",
                bottom: "20px",
                right: "10px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                zIndex: 1
            }}>
                <button
                    onClick={() => setZoom(prev => Math.min(prev + 1, 22))}
                    style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        fontSize: "18px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#333",
                        WebkitTapHighlightColor: "transparent",
                        outline: "none"
                    }}
                    aria-label="Zoom In"
                >
                    +
                </button>
                <button
                    onClick={() => setZoom(prev => Math.max(prev - 1, 1))}
                    style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        fontSize: "18px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#333",
                        WebkitTapHighlightColor: "transparent",
                        outline: "none"
                    }}
                    aria-label="Zoom Out"
                >
                    −
                </button>
                {enableCurrentLocation && (
                    <button
                        onClick={handleRecenter}
                        style={{
                            width: "30px",
                            height: "30px",
                            borderRadius: "4px",
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#2185d0",
                            WebkitTapHighlightColor: "transparent",
                            outline: "none"
                        }}
                        aria-label="My Location"
                    >
                        <Icon name="location arrow" style={{ margin: 0 }} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default CustomMap;
